// @file A fat module with
// - Post CRUD-related functions acting as a black-box to backend APIs for post operations.
// - Helpers related to abovementioned purpose.
import { trackEvent } from '@@/bits/analytics'
import { captureMessage } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { POST_BODY_CHARACTER_LIMIT, POST_SUBJECT_CHARACTER_LIMIT } from '@@/bits/numbers'
import { isPostEmpty } from '@@/bits/post_properties'
import PromiseQueue from '@@/bits/promise_queue'
import { NETWORK_SAVE_POST_FAILED, SAVE_CHANGES_FAILED } from '@@/bits/snackbar_helper'
import { isSortingBy, ManualSortByArray, WallWishSortByTypes } from '@@/bits/surface_settings_helper'
import { SnackbarNotificationType, SurfaceFilter } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { WishApi } from '@@/surface/api/wish'
import { getSortIndex } from '@@/surface/sorter'
import type { AccumulatedReactionData, Cid, HashId, Id, Post, PostAttributes, UpdatePostArgs } from '@@/types'
import type { PostState } from '@@/vuexstore/modules/post'
import type { RootState } from '@@/vuexstore/surface/types'
import type { PostColor } from '@padlet/arvo'
import { cloneDeep, debounce } from 'es-toolkit'
import { uniqueId } from 'es-toolkit/compat'
import type { ActionContext, Commit, Dispatch } from 'vuex'
/********************************
 * CONSTANTS
 ********************************/
const VALID_COLORS: PostColor[] = ['red', 'orange', 'green', 'blue', 'purple']
const POST_SHUFFLE_SEED = Math.random()

// Post actions in the post menu.
enum PostActionType {
  AddPostBefore = 'AddPostBefore',
  AddPostAfter = 'AddPostAfter',
  ExpandPost = 'ExpandPost',
  OpenPostInNewTab = 'OpenPostInNewTab',
  ClosePoll = 'ClosePoll',
  OpenPoll = 'OpenPoll',
  ExportPollResultsCSV = 'ExportPollResultsCSV',
  CopyPostLink = 'CopyPostLink',
  OpenAttachment = 'OpenAttachment',
  DownloadAttachment = 'DownloadAttachment',
  CopyAttachmentLink = 'CopyAttachmentLink',
  StartSlideshowFromThisPost = 'StartSlideshowFromThisPost',
  StartFieldTripFromThisPost = 'StartFieldTripFromThisPost',
  OpenLocationInMapApp = 'OpenLocationInMapApp',
  CopyLocationToClipboard = 'CopyLocationToClipboard',
  EditPost = 'EditPost',
  ConnectToPost = 'ConnectToPost',
  DisconnectFromPost = 'DisconnectFromPost',
  BringToFront = 'BringToFront',
  SendToBack = 'SendToBack',
  TransferPostToAnotherPadlet = 'TransferPost',
  CopyPostToAnotherPadlet = 'DuplicatePost',
  DuplicatePostToCurrentWall = 'DuplicatePostToCurrentWall',
  SetAsPadletCover = 'SetAsPadletCover',
  UnsetAsPadletCover = 'UnsetAsPadletCover',
  DeletePost = 'DeletePost',
  ChangePostColor = 'ChangePostColor',
  ReportPost = 'ReportPost',
  PinPost = 'PinPost',
  UnpinPost = 'UnpinPost',
  MakeAdminOnly = 'MakeAdminOnly',
  RemoveAdminOnly = 'RemoveAdminOnly',
  // Only for mobile app native post action menu
  SharePostLink = 'SharePostLink',
  ShareAttachmentLink = 'ShareAttachmentLink',
  ShareStaticAttachment = 'ShareStaticAttachment',
}

/********************************
 * HELPERS
 ********************************/
// HELPERS
const isNew = (post: Post | PostAttributes): boolean => !post.id
const hasAttachment = (post: Post): boolean => {
  return !(post.wish_content?.attachment_props == null) || !!post.attachment
}

const checkIfShouldPostShow = (post: Post, filter: SurfaceFilter): boolean => {
  if (filter === SurfaceFilter.All) return true
  const isPublished = post.published ?? true
  return filter === SurfaceFilter.Published ? isPublished : !isPublished
}

const filterPostWithFilter = (postEntitiesByCid: Record<Cid, Post>, filter: SurfaceFilter): Record<Cid, Post> => {
  if (filter === SurfaceFilter.Published || filter === SurfaceFilter.Submitted) {
    return Object.keys(postEntitiesByCid)
      .filter((cid) => checkIfShouldPostShow(postEntitiesByCid[cid], filter))
      .reduce((res, key) => ((res[key] = postEntitiesByCid[key]), res), {})
  }
  return postEntitiesByCid
}

function getPostCidsToDeleteInPage(
  isFirstPage: boolean,
  isLastPage: boolean,
  newPosts: any[],
  newPostEntitiesByCid: any,
): Cid[] {
  // the array returned from the back end is guaranteed to be ordered by id (DESC)
  const newPostIdList = newPosts.map((post) => post.attributes.id)

  // last page should cover the smallest post id
  const lookupRangeStart = isLastPage ? 0 : newPostIdList[newPosts.length - 1]

  // first page should cover the biggest post id
  const lookupRangeEnd = isFirstPage ? Infinity : newPostIdList[0]

  return Object.keys(newPostEntitiesByCid).filter((postCid: string): boolean => {
    const post = newPostEntitiesByCid[postCid]

    // Keep unsaved posts
    if (isNew(post)) {
      return false
    }

    const postId = newPostEntitiesByCid[postCid].id

    // Keep everything not inside the page's range
    if (postId > lookupRangeEnd || postId < lookupRangeStart) {
      return false
    }

    // Anything that's inside the page's range but not in newPostIdList
    // means they are deleted in the backend but the front end does not know yet
    return !newPostIdList.includes(postId)
  })
}

// Comparison for (sort_index DESC NULLS LAST, id DESC).
// Returns negative if a sorts before b, positive if a sorts after b, 0 if equal.
function compareSortOrder(
  a: { sort_index: number | null; id: number },
  b: { sort_index: number | null; id: number },
): number {
  if (a.sort_index == null && b.sort_index == null) return b.id - a.id
  if (a.sort_index == null) return 1
  if (b.sort_index == null) return -1
  if (a.sort_index !== b.sort_index) return b.sort_index - a.sort_index
  return b.id - a.id
}

// Stale-post detection for pages ordered by sort_index DESC NULLS LAST, id DESC.
// Mirrors getPostCidsToDeleteInPage but uses sort_index ranges instead of id ranges.
function getPostCidsToDeleteInPageBySortIndex(
  isFirstPage: boolean,
  isLastPage: boolean,
  newPosts: any[],
  newPostEntitiesByCid: any,
): Cid[] {
  if (newPosts.length === 0) return []

  const newPostIdSet = new Set<number>(newPosts.map((p) => p.attributes.id))
  const firstPost = newPosts[0].attributes
  const lastPost = newPosts[newPosts.length - 1].attributes

  return Object.keys(newPostEntitiesByCid).filter((postCid: string): boolean => {
    const post = newPostEntitiesByCid[postCid]
    if (isNew(post)) return false

    const candidate = { sort_index: post.sort_index as number | null, id: post.id as number }

    // Upper bound: exclude posts that sort before firstPost — they belong to an earlier page.
    if (!isFirstPage && compareSortOrder(candidate, firstPost) < 0) return false

    // Lower bound: exclude posts that sort after lastPost — they belong to a later page.
    if (!isLastPage && compareSortOrder(candidate, lastPost) > 0) return false

    return !newPostIdSet.has(post.id)
  })
}

// The reconciliation helper must match the server's actual pagination ordering. The server
// switches between sort_index DESC and id DESC depending on wall size (see
// SerializedPaginatedWallWishesFetcher::SORT_INDEX_WISHES_LIMIT) and signals which one it used
// via meta.pagination_order. Using the wrong helper for the response ordering causes the
// page's first/last post to bound a meaningless range and incorrectly flag posts as stale.
type PaginationOrder = 'sort_index' | 'id'

function parsePaginationOrder(meta: Record<string, unknown> | undefined): PaginationOrder | undefined {
  const value = meta?.pagination_order ?? meta?.paginationOrder
  return value === 'sort_index' || value === 'id' ? value : undefined
}

function selectPostCidsToDelete(
  paginationOrder: PaginationOrder,
  isFirstPage: boolean,
  isLastPage: boolean,
  newPosts: any[],
  newPostEntitiesByCid: any,
): Cid[] {
  return paginationOrder === 'sort_index'
    ? getPostCidsToDeleteInPageBySortIndex(isFirstPage, isLastPage, newPosts, newPostEntitiesByCid)
    : getPostCidsToDeleteInPage(isFirstPage, isLastPage, newPosts, newPostEntitiesByCid)
}

// reconcile newly fetched post data with current state to get the correct state
function reconcilePostsAfterFetch(context: any, newPosts: any[], options: Record<string, any>): Record<Cid, Post> {
  const { getters, rootGetters } = context

  const { isFirstPage, isLastPage, shouldResetState, paginationOrder } = options
  const getPostById = getters.getPostByServerId

  const isOnlyPage = isFirstPage && isLastPage
  const newPostEntitiesByCid = shouldResetState || isOnlyPage ? {} : { ...getters.postEntitiesByCid }

  // When we are just updating the state (no reset), there is 1 case we should care about:
  // Auto refresh is triggered after our page goes to the background and then comes to the top again (e.g. user switch browser tabs)
  // There maybe records deleted in the backend but not in the frontend => delete them
  // skip this check for the initial posts fetch when the page is first loaded
  if (!shouldResetState && getters.isInitialPaginatedDataLoadDone && newPosts.length > 0) {
    // The legacy flip+sort guess is only a fallback for responses that predate the explicit
    // pagination_order field (cached first-page payloads, realtime/fetchWishes). New REST
    // responses always carry the field.
    const effectivePaginationOrder: PaginationOrder =
      paginationOrder ??
      (ManualSortByArray.includes(rootGetters.wishSortBy) && isAppUsing('loadWishesViaSortBy')
        ? 'sort_index'
        : 'id')
    const cidsToDelete = selectPostCidsToDelete(
      effectivePaginationOrder,
      isFirstPage,
      isLastPage,
      newPosts,
      newPostEntitiesByCid,
    )
    cidsToDelete.forEach((cid: string): void => {
      delete newPostEntitiesByCid[cid]
    })
  }

  newPosts.forEach((postData) => {
    if (getters.isPostMarkedAsDeleted(postData.attributes)) {
      delete newPostEntitiesByCid[addCidIfAbsent(postData.attributes).cid]
      return
    }

    const post = postData.attributes
    // In rare cases some posts aren't saved with a sort_index. We assign them one on fetch.
    if (post.sort_index == null) {
      post.sort_index = getSortIndex()
    }

    const existingPost = getPostById(post.id)

    if (!existingPost) {
      // Add new post
      const postWithCid = addCidIfAbsent(post)
      newPostEntitiesByCid[postWithCid.cid] = postWithCid
    } else if (isPostRequestPending(existingPost, context)) {
      // Don't overwrite if there are changes that will be persisted
      newPostEntitiesByCid[existingPost.cid] = existingPost
    } else {
      // Update the post otherwise
      newPostEntitiesByCid[existingPost.cid] = {
        ...post,
        cid: existingPost.cid,
      }
    }
  })

  // Don't let unsaved posts disappear.
  getters.postsArray.forEach((post) => {
    if (isNew(post) && !newPostEntitiesByCid[post.cid]) {
      newPostEntitiesByCid[post.cid] = post
    }
  })

  return newPostEntitiesByCid
}

function sortPostsArray(
  context: any,
  postsArray: Post[],
  sortFunctions: {
    sortPostsBySortIndex: (posts: Post[]) => Post[]
    sortPostsByReactionSum: (
      posts: Post[],
      postIdsByReactionSum: Array<AccumulatedReactionData & { wishId: Id }>,
      constants?: { reactionsTotalSumAverage: number; confidenceNumber: number },
    ) => Post[]
    sortPostsByCreationDate: (posts: Post[]) => Post[]
    sortPostsBySubject: (posts: Post[], order: 'asc' | 'desc') => Post[]
    sortPostsByShuffle: (posts: Post[], seed: number) => Post[]
  },
): Post[] {
  const {
    sortPostsBySortIndex,
    sortPostsByReactionSum,
    sortPostsByCreationDate,
    sortPostsBySubject,
    sortPostsByShuffle,
  } = sortFunctions
  const { getters, rootGetters } = context
  const postsToShow = postsArray.filter(
    (post: Post) => post.id != null || !useSurfaceDraftsStore().isCidBeingDrafted(post.cid),
  )

  const wishSortBy: WallWishSortByTypes = (useSurfaceSettingsStore().isPreviewing as boolean)
    ? useSurfaceSettingsStore().wishSortBy
    : rootGetters.wishSortBy
  if (isSortingBy(wishSortBy, 'reactions')) {
    const sortedPosts = sortPostsByReactionSum(postsToShow, getters.accumulatedReactionsByWishId, {
      reactionsTotalSumAverage: getters.accumulatedReactionsTotalSumAverage,
      confidenceNumber: getters.accumulatedReactionsConfidenceNumber,
    })

    if (wishSortBy === WallWishSortByTypes.ReactionsDesc) {
      return sortedPosts.reverse()
    }

    return sortedPosts
  } else if (isSortingBy(wishSortBy, 'date_published')) {
    const sortedPosts = sortPostsByCreationDate(postsToShow)
    if (wishSortBy === WallWishSortByTypes.DatePublishedSortDesc) {
      return sortedPosts.reverse()
    }

    return sortedPosts
  } else if (isSortingBy(wishSortBy, 'post_subject')) {
    if (wishSortBy === 'post_subject_asc') {
      return sortPostsBySubject(postsToShow, 'asc')
    } else {
      return sortPostsBySubject(postsToShow, 'desc')
    }
  } else if (isSortingBy(wishSortBy, 'shuffle')) {
    return sortPostsByShuffle(postsToShow, POST_SHUFFLE_SEED)
  }

  return sortPostsBySortIndex(postsToShow)
}
/********************************
 * POST SAVING
 ********************************/

interface CommitAndDispatch {
  commit: Commit
  dispatch: Dispatch
  rootGetters?: any
}

enum PostConfiguration {
  All,
  BodyAndOrAttachment,
  BodyOrAttachment,
}

const DEFAULT_RETRY_DELAY = 500
function wrapMethodWithRetries<T>(
  method: () => Promise<T>,
  maxRetries = 0,
  delayInMs = DEFAULT_RETRY_DELAY,
): () => Promise<T> {
  let retriesLeft = maxRetries

  return async (): Promise<T> =>
    await new Promise((resolve, reject): void => {
      async function methodWithRetries(): Promise<any> {
        try {
          const result = await method()
          return resolve(result)
        } catch (e) {
          const isClientError = e.status >= 400 && e.status < 500
          const toRetry = !isClientError && retriesLeft > 0
          if (toRetry) {
            retriesLeft -= 1
            setTimeout((): void => {
              methodWithRetries()
            }, delayInMs)
          } else {
            reject(e)
          }
          return null
        }
      }
      methodWithRetries()
    })
}

// Cached PostSavers
const postSaverById: { [id: number]: PostSaver } = {}
const postSaverByCid: { [cid: string]: PostSaver } = {}

// These are the standard requests. You may define your own strings as custom keys.
enum PostRequestKey {
  Create = 'Create',
  // Update post with attributes at the time when request is enqueued
  UpdateCurrent = 'UpdateCurrent',
  // Update post with attributes at the time when request is executed
  UpdateLatest = 'UpdateLatest',
  Delete = 'Delete',
  Restore = 'Restore',
}

interface CustomSavePostRequest {
  key: string
  method: (post?: Post) => Promise<any>
}
interface Cancelable {
  cancel: () => void
  flush: () => void
}

/*****************************************************

PostSaver ensures that only one post update/create event is being made at a time.
After instantiation, you may call `save` on it, or enqueue a request:

```
const saver = new PostSaver(post, dispatch)
saver.save() // create immediately if new
// ...
saver.save() // does not fire request if one is still in progress
// ...
saver.save() // updates post if post has been persisted
saver.save() // debounces updates
saver.customSave({ key: 'A_POST_RELATED_REQUEST', method: () => { ... } }) // custom request queued
saver.save() // update request is queue after custom request
```

*****************************************************/

const networkErrorSavers = new Set<PostSaver>()
let networkErrorSnackbarUid: string | null = null

class PostSaver {
  public isUpdateDebouncing = false
  private readonly q: PromiseQueue
  private readonly dispatch!: Dispatch
  private readonly getters!: any
  // Store both id and cid so that we can use the same queue before and after the post is saved
  private postId!: Id | null
  private postHashid!: HashId | null
  private readonly postCid!: Cid
  private _debouncedEnqueueUpdate!: null | ((() => void) & Cancelable) // lazily created

  public constructor({ id, cid, hashid }: Post, { dispatch: PostModuleDispatch, getters: PostModuleGetters }) {
    this.postId = id ?? null
    this.postCid = cid
    this.postHashid = hashid ?? null
    this.dispatch = PostModuleDispatch
    this.getters = PostModuleGetters
    this.q = new PromiseQueue()
  }

  public get isIdle(): boolean {
    return this.q.isEmpty && !this.isUpdateDebouncing
  }

  public save(): Post {
    const post = this.post
    if (isPostEmpty(post)) return post

    if (post.sort_index == null) {
      post.sort_index = getSortIndex()
    }

    if (post.scheduled_at != null) {
      trackEvent('Posts', 'Scheduled post', null, null, {
        currentTime: new Date(),
        scheduledAt: post.scheduled_at, // track timestamp here; post.scheduled_at may have changed at analysis time
      })
    }

    if (isNew(post) && this.q.currentKey !== PostRequestKey.Create) {
      void this.q.enqueue(PostRequestKey.Create, this.createPost.bind(this))
    } else {
      this.debouncedEnqueueUpdate()
    }
    return post
  }

  public delete(options?: { softDelete: boolean }): void {
    this.q.enqueue(PostRequestKey.Delete, async () => await this.deletePost(options))
  }

  public async restore(): Promise<any> {
    return await this.q.enqueue(PostRequestKey.Restore, async () => await this.restorePost())
  }

  public async customSave({ key, method }: CustomSavePostRequest): Promise<any> {
    // Ensure post has been is created
    if (isNew(this.post) && this.q.currentKey !== PostRequestKey.Create) {
      this.q.enqueue(PostRequestKey.Create, this.createPost.bind(this))
    }
    // Post should be updated to current state before the new request is made
    // Convert UpdateLatest request to an UpdateCurrent request to lock in the current state
    this.flushUpdates()
    if (this.q.lastQueuedKey === PostRequestKey.UpdateLatest) {
      this.q.unqueue()
      const currentPost = cloneDeep(this.post)
      const updateCurrentPost = async (): Promise<any> => await this.updatePost(currentPost)
      this.q.enqueue(PostRequestKey.UpdateCurrent, updateCurrentPost)
    }

    // Wrap method to pass in latest post. Request might require id but was queued before post was saved.
    const wrappedMethod = async (): Promise<any> => {
      // If post has been deleted, skip subsequent operations.
      if (this.isPostMarkedAsDeleted) return await Promise.resolve()
      return await method(this.post)
    }

    // Finally, queue the method.
    return await this.q.enqueue(key, wrappedMethod)
  }

  public flushUpdates() {
    if (this._debouncedEnqueueUpdate == null) return
    this._debouncedEnqueueUpdate.flush()
  }

  // PRIVATE

  // Get the latest post
  private get post(): Post {
    return this.getters.getLiveOrDeletedPost({ cid: this.postCid, id: this.postId })
  }

  private get postOrder(): Cid[] {
    return this.getters.postOrder
  }

  private get isPostMarkedAsDeleted(): Post {
    return this.getters.isPostMarkedAsDeleted({ cid: this.postCid, id: this.postId })
  }

  private debouncedEnqueueUpdate(): void {
    this.isUpdateDebouncing = true
    if (this._debouncedEnqueueUpdate == null) {
      this._debouncedEnqueueUpdate = debounce(this.enqueueUpdate, 500)
    }
    return this._debouncedEnqueueUpdate()
  }

  private enqueueUpdate(): void {
    this.isUpdateDebouncing = false
    if (this.q.lastQueuedKey === PostRequestKey.UpdateLatest) return
    this.q.enqueue(PostRequestKey.UpdateLatest, this.updatePost.bind(this))
  }

  private async createPost(): Promise<any> {
    if (this.isPostMarkedAsDeleted) return

    const post = this.post

    // By the time the queue executes this, the post may have already been created.
    if (!isNew(post)) return

    // Get the true post order from the current postOrder that is in the store state.
    // Then get the array of postOrder that only contains the cid of the current postOrder
    // And only posts with ID (not pending creation)
    const postCreationParams: Post & UpdatePostArgs = post
    try {
      const createPostWithRetries = wrapMethodWithRetries(
        async (): Promise<{ data: { attributes: Post & UpdatePostArgs } }> => await WishApi.create(postCreationParams),
        1,
      )
      const createPostResult = (await createPostWithRetries()).data.attributes
      const { id, hashid } = createPostResult

      void this.dispatch('createdPostSuccess', { ...post, ...createPostResult })
      this.dismissNetworkErrorSnackbar()
      this.postId = id as Id
      this.postHashid = hashid as HashId
      postSaverById[id as Id] = this

      return { id }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        this.dismissNetworkErrorSnackbar()
        this.showSaveFailureSnackbar()
        return
      }

      if (e.status === 0) {
        this.showNetworkErrorSnackbar()
      } else {
        this.dismissNetworkErrorSnackbar()
        this.showPostOperationErrorSnackbar(post)
      }
      const context = {
        response: e.message,
        attachment: post.attachment,
        requestBody: asciiSafeStringify(postCreationParams),
      }
      captureMessage(`CreatedPostError ${e.status || ''}`, { context })
      return null
    }
  }

  private async updatePost(post?: Post): Promise<any> {
    if (this.isPostMarkedAsDeleted) return

    // If not specific version of the post is provided, fetch the latest from the store
    const clientPost = post != null ? { ...post, id: this.post.id } : this.post
    if (isPostEmpty(clientPost)) return null

    /**
     * Some attachments include attributes that may contain
     * an `iframe` element with an `srcdoc` attribute, which is
     * flagged by Cloudflare as a potential threat.
     */
    delete clientPost.attachment_link
    try {
      const savedPost = (await WishApi.update(clientPost)).data.attributes // Already retries
      void this.dispatch('updatedPostSuccess', { ...clientPost, ...savedPost })
      this.dismissNetworkErrorSnackbar()
      return savedPost
    } catch (e) {
      const context = { response: e.message, postId: clientPost.id }
      if (e.status === 404) {
        this.dismissNetworkErrorSnackbar()
        void this.dispatch('refresh', null, { root: true }) // Refresh if post deleted
      } else if (e.status === 403) {
        this.dismissNetworkErrorSnackbar()
        this.showSaveFailureSnackbar()
        captureMessage('UpdatedPostError 403', { context })
      } else if (e.status === 0) {
        this.showNetworkErrorSnackbar()
        captureMessage(`UpdatedPostError network`, { context })
      } else {
        this.dismissNetworkErrorSnackbar()
        this.showPostOperationErrorSnackbar(clientPost)
        captureMessage(`UpdatedPostError ${e.status || ''}`, { context })
      }
      return null
    }
  }

  private async deletePost(options?: { softDelete: boolean }): Promise<void> {
    try {
      if (this.postHashid == null) {
        this.showSaveFailureSnackbar()
        captureMessage('DeletedPostError hashId is null', { context: { postId: this.postId } })
        return
      }
      const query: Record<string, string> = {}
      if (options?.softDelete != null) {
        query.soft_delete = String(options.softDelete)
      }
      await WishApi.delete({ wishHashid: this.postHashid }, { query })
    } catch (e) {
      const context = { response: e.message, postId: this.postId }
      if (e.status === 404) {
        void this.dispatch('refresh', null, { root: true })
      } else if (e.status === 403) {
        this.showSaveFailureSnackbar()
        captureMessage('DeletedPostError 403', { context })
      } else {
        this.showSaveFailureSnackbar()
        captureMessage(`DeletedPostError ${e.status || ''}`, { context })
      }
    }
  }

  private async restorePost(): Promise<PostAttributes | null> {
    if (this.postHashid == null) return null
    // handle error in undoable.ts
    return (await WishApi.restore({ wishHashid: this.postHashid })).data.attributes ?? null
  }

  private showPostOperationErrorSnackbar(post: Post): void {
    if ((post.subject ?? '').length > POST_SUBJECT_CHARACTER_LIMIT) {
      this.showSaveFailureSnackbar(__('Post subject character limit reached'))
    } else if ((post?.body ?? '').length > POST_BODY_CHARACTER_LIMIT) {
      this.showSaveFailureSnackbar(__('Post body character limit reached'))
    } else {
      this.showSaveFailureSnackbar()
    }
  }

  private showSaveFailureSnackbar(message?: string): void {
    if (message != null) {
      useGlobalSnackbarStore().setSnackbar({
        message,
        notificationType: SnackbarNotificationType.error,
      })
    } else {
      useGlobalSnackbarStore().setSnackbar(SAVE_CHANGES_FAILED)
    }
  }

  private showNetworkErrorSnackbar(): void {
    networkErrorSavers.add(this)
    networkErrorSnackbarUid = useGlobalSnackbarStore().setSnackbar(
      {
        ...NETWORK_SAVE_POST_FAILED,
        notificationType: SnackbarNotificationType.error,
      },
      { allowDuplicate: false },
    )
  }

  private dismissNetworkErrorSnackbar(): void {
    networkErrorSavers.delete(this)
    if (networkErrorSavers.size === 0 && networkErrorSnackbarUid != null) {
      useGlobalSnackbarStore().removeSnackbar(networkErrorSnackbarUid)
      networkErrorSnackbarUid = null
    }
  }
}

// Assumes post has a Cid.
function getOrCreatePostSaver(post: Post, context): PostSaver {
  // Prefer to look up post by server id since cid changes after initial creation.
  // Assume that when post is successfully saved, it will be populated in postSaverById.
  let saver = (post.id && postSaverById[post.id]) || postSaverByCid[post.cid]
  if (saver) return saver

  saver = new PostSaver(post, context)
  if (post.id) {
    postSaverById[post.id] = saver
  } else {
    postSaverByCid[post.cid] = saver
  }
  return saver
}

function isPostRequestPending(post: Post, context): boolean {
  const postSaver = getOrCreatePostSaver(post, context)
  return !postSaver.isIdle
}

const savePost = (context: ActionContext<PostState, RootState>, post: PostAttributes & UpdatePostArgs): Post => {
  const { dispatch, getters } = context
  // Assumes that: (1) post has a cid
  // Ensures that: (1) post is added to the store, (2) post in the store is latest version
  if (post.cid == null) {
    throw new Error('helpers/post.ts#savePost: cid must be defined')
  }
  // check whether post has already been created
  if (getters.isExistingPost(post.cid) === false) {
    // creating a new post from draft: add a new post to store.
    void dispatch('addPost', post)
  } else {
    // update an existing post: we directly update the store.
    void dispatch('updatePostInStore', post)
  }

  return getOrCreatePostSaver(post as Post, context).save()
}

async function customSavePost(context, post: Post, request: CustomSavePostRequest): Promise<any> {
  return await getOrCreatePostSaver(post, context).customSave(request)
}

function deletePost(context: CommitAndDispatch, post: Post, options?: { softDelete: boolean }): void {
  context.dispatch('removePost', post)
  getOrCreatePostSaver(post, context).delete(options)
}

async function restorePost(context: CommitAndDispatch, post: Post): Promise<void> {
  const restoredPost = await getOrCreatePostSaver(post, context).restore()
  context.dispatch('revivePost', { ...post, ...(restoredPost ?? {}) })
}

/**
 * Delete all posts using PadletApi abstraction
 */
function deleteAllPosts(context: CommitAndDispatch, posts: Post[], options?: { softDelete: boolean }): void {
  posts.forEach((post): void => {
    deletePost(context, post, options)
  })
}
/********************************
 * ID/CID HELPERS
 ********************************/
/**
 * Maintains the cid of a post if it's present. Generate one otherwise.
 */
function addCidIfAbsent(post: PostAttributes): Post {
  if (post.cid) return post as Post
  const cid = post.id ? `c${post.id}` : uniqueId('c_new')
  return { ...post, cid }
}

export {
  addCidIfAbsent,
  checkIfShouldPostShow,
  compareSortOrder,
  customSavePost,
  deleteAllPosts,
  deletePost,
  filterPostWithFilter,
  getOrCreatePostSaver,
  getPostCidsToDeleteInPage,
  getPostCidsToDeleteInPageBySortIndex,
  hasAttachment,
  isNew,
  parsePaginationOrder,
  PostActionType,
  PostConfiguration,
  reconcilePostsAfterFetch,
  restorePost,
  savePost,
  selectPostCidsToDelete,
  sortPostsArray,
  VALID_COLORS,
}
export type { CustomSavePostRequest, PaginationOrder }
